import {
    INCENTIVE_DISPLAY_STATE,
    INCENTIVE_TYPE,
    SP_STATUS_KEY_PAYMENT_FAILED,
    STUDY_PERSON_INCENTIVE_STATUS
} from 'utils/constants';

/**
 * Derives the incentive display state from a study person record.
 *
 * The display state determines which Badge variant to render in the table
 * and participant popup. It is derived from multiple fields:
 *
 * - **failed** — participant status is "Payment Failed" (`status === 8`)
 * - **pending** — marked as paid via a first-party channel but `incentive_sent_at`
 *   is not yet set (payment requested, waiting ~7 days for execution)
 * - **paid** — `incentive_status` is `'paid'` and the payment has been sent
 * - **null** — not yet paid; no badge should be rendered
 *
 * @param {Object} studyPerson - A study_person record from the API.
 * @param {string} [studyPerson.incentive_status] - 'paid' or 'unpaid'.
 * @param {string|null} [studyPerson.incentive_sent_at] - ISO date when payment was sent; null while pending.
 * @param {number} [studyPerson.incentive_type] - Numeric payment channel (see INCENTIVE_TYPE).
 * @param {number} [studyPerson.status] - Participant status key (e.g. SP_STATUS_KEY_PAYMENT_FAILED = 8).
 * @returns {import('utils/constants').INCENTIVE_DISPLAY_STATE[keyof import('utils/constants').INCENTIVE_DISPLAY_STATE]|null}
 */
export const getIncentiveDisplayState = studyPerson => {
    if (studyPerson.status === SP_STATUS_KEY_PAYMENT_FAILED) {
        return INCENTIVE_DISPLAY_STATE.FAILED;
    }

    if (studyPerson.incentive_status !== STUDY_PERSON_INCENTIVE_STATUS.PAID) {
        return null;
    }

    const isFirstParty =
        studyPerson.incentive_type === INCENTIVE_TYPE.FIRST_PARTY ||
        studyPerson.incentive_type === INCENTIVE_TYPE.FIRST_PARTY_VIA_TREMENDOUS;

    if (isFirstParty && !studyPerson.incentive_sent_at) {
        return INCENTIVE_DISPLAY_STATE.PENDING;
    }

    return INCENTIVE_DISPLAY_STATE.PAID;
};
